/**
 * Shared formatting utilities
 *
 * Common formatting functions used across the application.
 */

/**
 * Format large numbers with smart rounding based on magnitude
 * No decimals for numbers >= 1000, only use decimals for small numbers
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return Math.round(num / 1_000_000) + 'M';
  } else if (num >= 1_000) {
    return Math.round(num / 1_000) + 'K';
  } else if (num >= 10) {
    return Math.round(num).toString();
  } else if (num >= 1) {
    return num.toFixed(1);
  } else {
    return num.toFixed(2);
  }
}

/**
 * Format address with truncation
 * @param address - Full address string
 * @param startChars - Number of characters to show at start (default: 6)
 * @param endChars - Number of characters to show at end (default: 4)
 */
export function formatAddress(address: string, startChars = 6, endChars = 4): string {
  if (!address || address.length <= startChars + endChars + 3) {
    return address;
  }
  return `${address.substring(0, startChars)}...${address.substring(address.length - endChars)}`;
}

/**
 * Format gas value with appropriate units
 */
export function formatGas(gas: number): string {
  if (gas >= 1_000_000_000) {
    return (gas / 1_000_000_000).toFixed(2) + 'B';
  } else if (gas >= 1_000_000) {
    return (gas / 1_000_000).toFixed(2) + 'M';
  } else if (gas >= 1_000) {
    return (gas / 1_000).toFixed(2) + 'K';
  }
  return gas.toFixed(0);
}

/**
 * Format timestamp to locale string
 */
export function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

/**
 * Format percentage with specified decimal places
 */
export function formatPercent(value: number, decimals = 1): string {
  return value.toFixed(decimals) + '%';
}

/**
 * Format bytes to human readable size
 */
export function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) {
    return (bytes / 1_000_000_000).toFixed(2) + ' GB';
  } else if (bytes >= 1_000_000) {
    return (bytes / 1_000_000).toFixed(2) + ' MB';
  } else if (bytes >= 1_000) {
    return (bytes / 1_000).toFixed(2) + ' KB';
  }
  return bytes + ' B';
}
